import { JTinyMCEExtensionsBuilders } from 'jamespot-react-components';
import { JIACapabilities } from 'jamespot-user-api';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

export type JiaTextEnhanceQuery = {
    resolve: (value: string | PromiseLike<string>) => void;
    reject: (reason?: any) => void;
    capability: JIACapabilities;
    text: string;
};

export const useAI = () => {
    const [query, setQuery] = useState<JiaTextEnhanceQuery | undefined>();
    const intl = useIntl();

    const isActive = useMemo(() => {
        return (
            J.hooks?.MagicWandHook?.isActive &&
            J.hooks?.MagicWandHook?.menuItems &&
            J.hooks.MagicWandHook.menuItems.length
        );
    }, []);

    const PrepareAndSetQuery = useCallback((query: JiaTextEnhanceQuery) => {
        const internalResolve = (value: string | PromiseLike<string>) => {
            query.resolve(value);
            setQuery(undefined);
        };
        const internalReject = (value: any) => {
            query.reject(value);
            setQuery(undefined);
        };
        setQuery({
            ...query,
            reject: internalReject,
            resolve: internalResolve,
        });
    }, []);

    const fetch = useCallback(
        (text: string, capability: JIACapabilities) => {
            return new Promise<string>((resolve, reject) => {
                PrepareAndSetQuery({
                    reject,
                    resolve,
                    text,
                    capability,
                });
            });
        },
        [PrepareAndSetQuery],
    );

    const AICapabilities = useMemo(() => {
        return {
            develop: (text: string) => fetch(text, 'develop'),
            summarize: (text: string) => fetch(text, 'summarize'),
            tldr: (text: string) => fetch(text, 'tldr'),
            en: (text: string) => fetch(text, 'en'),
            es: (text: string) => fetch(text, 'es'),
            formal: (text: string) => fetch(text, 'formal'),
            funny: (text: string) => fetch(text, 'funny'),
        };
    }, [fetch]);

    const ext = useMemo(() => JTinyMCEExtensionsBuilders.jia(AICapabilities, intl), [intl, AICapabilities]);

    return { query, ext, isActive } as const;
};
